import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  Download,
  Eye,
  Pencil,
  Plus,
  Printer,
  RotateCcw,
  Search,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { sortByColumn, useTableSort } from "@/shared/hooks/useTableSort";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TableToolbar } from "../components/TableToolbar";
import { EmployeeAvatar } from "../components/EmployeeAvatar";
import { SortableTableHead } from "../components/SortableTableHead";
import { StatusBadge } from "../components/StatusBadge";
import { TablePagination } from "../components/TablePagination";
import { FormField, inputClass, selectClass } from "../components/FormField";
import { companyOptions, provinceOptions, branchOptions, statusOptions } from "../data";
import { useAdminEmployees } from "../hooks/useAdminEmployees";
import { parseEmployeeRouteId } from "../lib/employeeRouteId";

function normalizeProvince(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/-/g, " ");
}

function employeeMatchesProvince(employeeProvince: string, filterValue: string): boolean {
  if (!filterValue) return true;
  const label = provinceOptions.find((option) => option.value === filterValue)?.label ?? filterValue;
  const hay = normalizeProvince(employeeProvince);
  return [filterValue, label].some((needle) => {
    const normalized = normalizeProvince(needle);
    return normalized !== "" && (hay.includes(normalized) || normalized.includes(hay));
  });
}

export function EmployeesPage() {
  const { t } = useTranslation();
  const paginationLabels = t.safecheck.tablePagination;
  const { exportTable, printPage } = usePageActions();
  const { employees, total, loading, error, usingApi, reload } = useAdminEmployees();
  const [topSearch, setTopSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [provinceFilter, setProvinceFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [systemIdFilter, setSystemIdFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const { sortKey, sortDirection, toggleSort } = useTableSort("name", "asc");

  useEffect(() => {
    if (!usingApi) return;
    void reload({
      province: provinceFilter || undefined,
      branch: branchFilter || undefined,
      system_id: systemIdFilter.trim() || undefined,
      status: statusFilter || undefined,
      search: topSearch.trim() || undefined,
    });
  }, [
    usingApi,
    reload,
    provinceFilter,
    branchFilter,
    systemIdFilter,
    statusFilter,
    topSearch,
  ]);

  const handleSort = (key: string) => {
    toggleSort(key);
    setPage(1);
  };

  const filtered = useMemo(() => {
    return employees.filter((employee) => {
      const byTopSearch = topSearch
        ? `${employee.name} ${employee.systemId} ${employee.company} ${employee.province} ${employee.branch}`
            .toLowerCase()
            .includes(topSearch.toLowerCase())
        : true;
      const byCompany = companyFilter
        ? employee.company.toLowerCase().includes(companyFilter.toLowerCase())
        : true;
      const byProvince = employeeMatchesProvince(employee.province, provinceFilter);
      const byBranch = branchFilter
        ? employee.branch.toLowerCase().includes(branchFilter.toLowerCase())
        : true;
      const bySystemId = systemIdFilter
        ? employee.systemId.toLowerCase().includes(systemIdFilter.toLowerCase())
        : true;
      const byStatus = statusFilter ? employee.status === statusFilter : true;
      const byTableSearch = tableSearch
        ? `${employee.name} ${employee.systemId} ${employee.companyMatricule} ${employee.company} ${employee.province} ${employee.branch}`
            .toLowerCase()
            .includes(tableSearch.toLowerCase())
        : true;

      return byTopSearch && byCompany && byProvince && byBranch && bySystemId && byStatus && byTableSearch;
    });
  }, [employees, topSearch, companyFilter, provinceFilter, branchFilter, systemIdFilter, statusFilter, tableSearch]);

  const sorted = useMemo(
    () =>
      sortByColumn(filtered, sortKey, sortDirection, (employee, key) => {
        if (key === "name") return employee.name;
        if (key === "systemId") return employee.systemId;
        if (key === "matricule") return employee.companyMatricule;
        if (key === "company") return employee.company;
        if (key === "province") return employee.province;
        return "";
      }),
    [filtered, sortKey, sortDirection],
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const pageData = sorted.slice(start, start + pageSize);

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const columnDefs = [
    { key: "photo", label: t.safecheck.employees.columns.photo, sortable: false },
    { key: "name", label: t.safecheck.employees.columns.name, sortable: true },
    { key: "systemId", label: t.safecheck.employees.columns.systemId, sortable: true },
    { key: "matricule", label: t.safecheck.employees.columns.matricule, sortable: true },
    { key: "company", label: t.safecheck.employees.columns.company, sortable: true },
    { key: "province", label: t.safecheck.employees.columns.province, sortable: true },
  ] as const;

  const handleExport = () => {
    exportTable(
      "employes.csv",
      ["Nom", "ID Système", "Entreprise", "Province", "Agence", "Statut"],
      filtered.map((employee) => [
        employee.name,
        employee.systemId,
        employee.company,
        employee.province,
        employee.branch,
        employee.status,
      ]),
    );
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t.safecheck.nav.employees },
        ]}
      />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-xl font-bold text-foreground">{t.safecheck.employees.title}</h1>
        <Link
          to="/dashboard/employees/create"
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
        >
          <Plus size={16} />
          {t.safecheck.employees.add}
        </Link>
      </div>

      {error && (
        <p className="mb-3 rounded-xl border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      )}
      {loading && <p className="mb-3 text-sm text-muted">Chargement des employés…</p>}
      {usingApi && !loading && !error && (
        <p className="mb-3 text-xs text-muted-foreground">{total} employé(s) — base de données</p>
      )}

      <div className="theme-card p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <input
            value={topSearch}
            onChange={(event) => {
              setTopSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Rechercher..."
            className={`${inputClass} w-full max-w-[260px]`}
          />
          <button
            type="button"
            onClick={printPage}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
          >
            <Printer size={16} />
            {t.safecheck.print}
          </button>
          <button
            type="button"
            onClick={handleExport}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
          >
            <Download size={16} />
            Exporter CSV
          </button>
          <button
            type="button"
            onClick={() => {
              setTopSearch("");
              setCompanyFilter("");
              setProvinceFilter("");
              setBranchFilter("");
              setSystemIdFilter("");
              setStatusFilter("");
              setTableSearch("");
              setPage(1);
            }}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-card-muted"
          >
            <RotateCcw size={16} />
          </button>
        </div>

        <div className="rounded-xl border border-border p-4">
          <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Search size={14} />
            <span>{t.safecheck.filters}</span>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <FormField label={t.safecheck.fields.company}>
              <select
                value={companyFilter}
                onChange={(event) => {
                  setCompanyFilter(event.target.value);
                  setPage(1);
                }}
                className={selectClass}
              >
                {companyOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label={t.safecheck.fields.province}>
              <select
                value={provinceFilter}
                onChange={(event) => {
                  setProvinceFilter(event.target.value);
                  setPage(1);
                }}
                className={selectClass}
              >
                {provinceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label={t.safecheck.fields.branch}>
              <select
                value={branchFilter}
                onChange={(event) => {
                  setBranchFilter(event.target.value);
                  setPage(1);
                }}
                className={selectClass}
              >
                {branchOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label={t.safecheck.fields.systemId}>
              <input
                value={systemIdFilter}
                onChange={(event) => {
                  setSystemIdFilter(event.target.value);
                  setPage(1);
                }}
                className={inputClass}
                placeholder="Ex: SFC0001"
              />
            </FormField>
          </div>
          <div className="mt-4 max-w-xs">
            <FormField label={t.safecheck.fields.status}>
              <select
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  setPage(1);
                }}
                className={selectClass}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FormField>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
            >
              <Search size={15} />
              {t.safecheck.filter}
            </button>
            <button
              type="button"
              onClick={() => {
                setCompanyFilter("");
                setProvinceFilter("");
                setBranchFilter("");
                setSystemIdFilter("");
                setStatusFilter("");
                setPage(1);
              }}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm text-muted-foreground hover:bg-card-muted"
            >
              <RotateCcw size={15} />
              {t.safecheck.reset}
            </button>
          </div>
        </div>

        <TableToolbar
          search={tableSearch}
          onSearchChange={(value) => {
            setTableSearch(value);
            setPage(1);
          }}
          searchPlaceholder={t.safecheck.search}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          showLabel={t.safecheck.show}
          entriesLabel={t.safecheck.entries}
        />

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/40">
                {columnDefs.map((column) => (
                  <SortableTableHead
                    key={column.key}
                    label={column.label}
                    columnKey={column.key}
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={handleSort}
                    sortable={column.sortable}
                  />
                ))}
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {pageData.map((employee) => {
                const recordId = parseEmployeeRouteId(employee.id);
                const canOpen = !Number.isNaN(recordId) && recordId > 0;
                return (
                <tr key={employee.id} className="border-b border-border last:border-0 hover:bg-card-muted/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <ChevronRight size={14} className="text-muted" />
                      <EmployeeAvatar name={employee.name} photo={employee.photo} size={36} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-foreground">{employee.name}</td>
                  <td className="px-4 py-3">
                    <StatusBadge variant="purple">{employee.systemId}</StatusBadge>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{employee.companyMatricule}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{employee.company}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{employee.province}</td>
                  <td className="px-4 py-3">
                    {canOpen ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          to={`/dashboard/employees/${recordId}`}
                          className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-sm text-foreground hover:bg-card-muted"
                        >
                          <Eye size={14} />
                          Voir
                        </Link>
                        <Link
                          to={`/dashboard/employees/${recordId}/edit`}
                          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                        >
                          <Pencil size={14} />
                          Modifier
                        </Link>
                      </div>
                    ) : (
                      <span className="text-xs text-muted">—</span>
                    )}
                  </td>
                </tr>
                );
              })}
              {pageData.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-muted">
                    Aucun employé trouvé.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={sorted.length}
          onPageChange={handlePageChange}
          labels={paginationLabels}
        />
      </div>
    </>
  );
}
